import { type ComputedRef, computed, type Ref } from "vue";
import { centsToDollars, dollarsToCents } from "@/mixins/helpers";
import {
  feeCheckboxIsOnFromSessionCents,
  type ProgramSessionConfigurableFeesModel,
  type SessionFeeSnapshot,
} from "./types";

/** Which `programSession` cent field this currency input reads and writes. */
export type ProgramSessionFeeCentsFieldKey = "application_fee_in_cents" | "confirmation_deposit_in_cents";

/**
 * Read-only dollars value for a PrimeVue currency `InputNumber` bound to one `*_in_cents` field.
 * When the fee checkbox is off (no positive cents stored), returns `0`.
 */
export const sessionFeeDollarsForInput = (
  programSession: Ref<ProgramSessionConfigurableFeesModel>,
  centsKey: ProgramSessionFeeCentsFieldKey,
): ComputedRef<number> => {
  return computed(() => {
    const cents = programSession.value[centsKey];
    if (!feeCheckboxIsOnFromSessionCents(cents)) {
      return 0;
    }
    const centsNumber = Number(cents);
    return centsToDollars(centsNumber);
  });
};

/**
 * Persists dollars from `InputNumber` into `programSession` cents (inverse of {@link sessionFeeDollarsForInput}).
 * Call from `@update:model-value` instead of using a writable computed setter.
 */
export const commitSessionFeeDollarsFromInput = (
  programSession: Ref<ProgramSessionConfigurableFeesModel>,
  centsKey: ProgramSessionFeeCentsFieldKey,
  val: number | null | undefined,
): void => {
  if (!feeCheckboxIsOnFromSessionCents(programSession.value[centsKey])) {
    return;
  }
  if (val === undefined || val === null) {
    programSession.value[centsKey] = undefined;
    return;
  }
  const num = Number(val);
  if (Number.isNaN(num) || num <= 0) {
    programSession.value[centsKey] = undefined;
    return;
  }
  programSession.value[centsKey] = dollarsToCents(num);
};

export const readSessionFeeSnapshot = (
  programSession: ProgramSessionConfigurableFeesModel,
  ownerEntityId: string | undefined,
): SessionFeeSnapshot => ({
  ownerEntityId,
  applicationFeeInCents: programSession.application_fee_in_cents,
  confirmationDepositInCents: programSession.confirmation_deposit_in_cents,
});

export const forceSessionFeesToZero = (programSession: ProgramSessionConfigurableFeesModel): void => {
  if (programSession.application_fee_in_cents !== 0) {
    programSession.application_fee_in_cents = 0;
  }
  if (programSession.confirmation_deposit_in_cents !== 0) {
    programSession.confirmation_deposit_in_cents = 0;
  }
};

export const restoreSessionFeesFromSnapshot = (
  programSession: ProgramSessionConfigurableFeesModel,
  snapshot: SessionFeeSnapshot | undefined,
  ownerEntityId: string,
): void => {
  if (snapshot && snapshot.ownerEntityId === ownerEntityId) {
    // Only over a forced 0, so anything the admin has typed since wins.
    if (programSession.application_fee_in_cents === 0) {
      programSession.application_fee_in_cents = snapshot.applicationFeeInCents;
    }
    if (programSession.confirmation_deposit_in_cents === 0) {
      programSession.confirmation_deposit_in_cents = snapshot.confirmationDepositInCents;
    }
  }
};
